import { applyPolyfills, defineCustomElements } from '@tdp/st-design-components/loader';
import { __rest } from 'tslib';
import React from 'react';
const dashToPascalCase = str => str.toLowerCase().split('-').map(segment => segment.charAt(0).toUpperCase() + segment.slice(1)).join('');
const camelToDashCase = str => str.replace(/([A-Z])/g, m => `-${m[0].toLowerCase()}`);
const attachProps = (node, newProps, oldProps = {}) => {
  // some test frameworks don't render DOM elements, so we test here to make sure we are dealing with DOM first
  if (node instanceof Element) {
    // add any classes in className to the class list
    const className = getClassName(node.classList, newProps, oldProps);
    if (className !== '') {
      node.className = className;
    }
    Object.keys(newProps).forEach(name => {
      if (name === 'children' || name === 'style' || name === 'ref' || name === 'class' || name === 'className' || name === 'forwardedRef') {
        return;
      }
      if (name.indexOf('on') === 0 && name[2] === name[2].toUpperCase()) {
        const eventName = name.substring(2);
        const eventNameLc = eventName[0].toLowerCase() + eventName.substring(1);
        if (!isCoveredByReact(eventNameLc)) {
          syncEvent(node, eventNameLc, newProps[name]);
        }
      } else {
        node[name] = newProps[name];
        const propType = typeof newProps[name];
        if (propType === 'string') {
          node.setAttribute(camelToDashCase(name), newProps[name]);
        } else {
          node[name] = newProps[name];
        }
      }
    });
  }
};
const getClassName = (classList, newProps, oldProps) => {
  const newClassProp = newProps.className || newProps.class;
  const oldClassProp = oldProps.className || oldProps.class;
  // map the classes to Maps for performance
  const currentClasses = arrayToMap(classList);
  const incomingPropClasses = arrayToMap(newClassProp ? newClassProp.split(' ') : []);
  const oldPropClasses = arrayToMap(oldClassProp ? oldClassProp.split(' ') : []);
  const finalClassNames = [];
  // loop through each of the current classes on the component
  // to see if it should be a part of the classNames added
  currentClasses.forEach(currentClass => {
    if (incomingPropClasses.has(currentClass)) {
      // add it as its already included in classnames coming in from newProps
      finalClassNames.push(currentClass);
      incomingPropClasses.delete(currentClass);
    } else if (!oldPropClasses.has(currentClass)) {
      // add it as it has NOT been removed by user
      finalClassNames.push(currentClass);
    }
  });
  incomingPropClasses.forEach(s => finalClassNames.push(s));
  return finalClassNames.join(' ');
};
/**
 * Checks if an event is supported in the current execution environment.
 * @license Modernizr 3.0.0pre (Custom Build) | MIT
 */
const isCoveredByReact = (eventNameSuffix, doc = document) => {
  const eventName = 'on' + eventNameSuffix;
  let isSupported = (eventName in doc);
  if (!isSupported) {
    const element = doc.createElement('div');
    element.setAttribute(eventName, 'return;');
    isSupported = typeof element[eventName] === 'function';
  }
  return isSupported;
};
const syncEvent = (node, eventName, newEventHandler) => {
  const eventStore = node.__events || (node.__events = {});
  const oldEventHandler = eventStore[eventName];
  // Remove old listener so they don't double up.
  if (oldEventHandler) {
    node.removeEventListener(eventName, oldEventHandler);
  }
  // Bind new listener.
  node.addEventListener(eventName, eventStore[eventName] = function handler(e) {
    if (newEventHandler) {
      newEventHandler.call(this, e);
    }
  });
};
const arrayToMap = arr => {
  const map = new Map();
  arr.forEach(s => map.set(s, s));
  return map;
};
const createForwardRef = (ReactComponent, displayName) => {
  const forwardRef = (props, ref) => {
    return React.createElement(ReactComponent, Object.assign({}, props, {
      forwardedRef: ref
    }));
  };
  forwardRef.displayName = displayName;
  return React.forwardRef(forwardRef);
};
const createReactComponent = tagName => {
  const displayName = dashToPascalCase(tagName);
  const ReactComponent = class extends React.Component {
    constructor(props) {
      super(props);
      this.wrapper = React.createRef();
    }
    componentDidMount() {
      this.componentDidUpdate(this.props);
    }
    componentDidUpdate(prevProps) {
      const node = this.wrapper.current;
      attachProps(node, this.props, prevProps);
    }
    render() {
      const _a = this.props,
        {
          children,
          style,
          className,
          forwardedRef,
          ref
        } = _a,
        cProps = __rest(_a, ["children", "style", "className", "forwardedRef", "ref"]);
      const propsToPass = Object.keys(cProps).reduce((acc, name) => {
        if (name.indexOf('on') === 0 && name[2] === name[2].toUpperCase()) {
          const eventName = name.substring(2).toLowerCase();
          if (isCoveredByReact(eventName)) {
            acc[name] = cProps[name];
          }
        }
        return acc;
      }, {});
      const newProps = Object.assign(Object.assign({}, propsToPass), {
        ref: this.wrapper,
        style
      });
      return React.createElement(tagName, newProps, children);
    }
    static get displayName() {
      return displayName;
    }
  };
  return createForwardRef(ReactComponent, displayName);
};
const TdpButton = createReactComponent('tdp-button');
const TdpCalendar = createReactComponent('tdp-calendar');
const TdpCheckbox = createReactComponent('tdp-checkbox');
const TdpChip = createReactComponent('tdp-chip');
const TdpDatePicker = createReactComponent('tdp-datepicker');
const TdpDropDown = createReactComponent('tdp-dropdown');
const TdpIcon = createReactComponent('tdp-icon');
const TdpMiniPaginator = createReactComponent('tdp-mini-paginator');
const TdpPriceDescription = createReactComponent('tdp-price-description');
const TdpPricePromo = createReactComponent('tdp-price-promo');
const TdpRadioButton = createReactComponent('tdp-radio-button');
const TdpRadioButonGroup = createReactComponent('tdp-radio-buton-group');
const TdpStepper = createReactComponent('tdp-stepper');
const TdpSwitch = createReactComponent('tdp-switch');
const TdpTextArea = createReactComponent('tdp-textarea');
const TdpTextField = createReactComponent('tdp-textfield');
applyPolyfills().then(() => {
  defineCustomElements(window);
});
export { TdpButton, TdpCalendar, TdpCheckbox, TdpChip, TdpDatePicker, TdpDropDown, TdpIcon, TdpMiniPaginator, TdpPriceDescription, TdpPricePromo, TdpRadioButonGroup, TdpRadioButton, TdpStepper, TdpSwitch, TdpTextArea, TdpTextField };